import { htmlToSlate, slateToHtml } from '@amelio/ui-library';

import { CommunicationBlockId, type CommunicationsLayerBlockIdValue } from './communicationBlocks';

import type { CycleCommunicationBlockPayload } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import type { CommunicationEmailAudienceValue } from '@/pages/CycleEvaluation/Communications/components/CommunicationEmailCard/CommunicationEmailCard';
import type {
    CycleCommunicationBlock,
    CycleCommunicationEmail,
} from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { EmailDefaults } from '@/utils/emailDefaults360';
import type { Descendant } from 'slate';

import {
    CycleCommunicationMilestoneType,
    CycleCommunicationRecipientType,
    type CycleCommunicationMilestoneTypeValue,
    type CycleCommunicationRecipientTypeValue,
} from '@/constants/cycle-evaluation/CycleCommunication';
import { CommunicationEmailAudience } from '@/pages/CycleEvaluation/Communications/components/CommunicationEmailCard/CommunicationEmailCard';
import { getEmptyEmailMessage } from '@/utils/schedule';

/**
 * Spelled out rather than derived from the string casing: a computed mapping would drift silently
 * the day either side is renamed, and nothing would fail to compile.
 */
export const COMMUNICATION_MILESTONE_TYPE_BY_BLOCK_ID: Readonly<
    Record<CommunicationsLayerBlockIdValue, CycleCommunicationMilestoneTypeValue>
> = {
    [CommunicationBlockId.PROJECT_KICK_OFF]: CycleCommunicationMilestoneType.PROJECT_KICK_OFF,
    [CommunicationBlockId.MANAGER_ASSESSMENT_LAUNCH]:
        CycleCommunicationMilestoneType.MANAGER_ASSESSMENT_LAUNCH,
    [CommunicationBlockId.MANAGER_DEADLINE]: CycleCommunicationMilestoneType.MANAGER_DEADLINE,
    [CommunicationBlockId.DISCUSSION_EMAIL]: CycleCommunicationMilestoneType.DISCUSSION_EMAIL,
    [CommunicationBlockId.EVALUATION_CYCLE_CLOSURE]:
        CycleCommunicationMilestoneType.EVALUATION_CYCLE_CLOSURE,
};

export const COMMUNICATION_RECIPIENT_BY_AUDIENCE: Readonly<
    Record<CommunicationEmailAudienceValue, CycleCommunicationRecipientTypeValue>
> = {
    [CommunicationEmailAudience.MANAGER]: CycleCommunicationRecipientType.MANAGER,
    [CommunicationEmailAudience.EMPLOYEE]: CycleCommunicationRecipientType.EMPLOYEE,
};

export const findExistingCommunicationBlock = (
    communications: readonly CycleCommunicationBlock[] | undefined,
    blockId: CommunicationsLayerBlockIdValue,
): CycleCommunicationBlock | undefined =>
    communications?.find(
        (block) => block.milestoneType === COMMUNICATION_MILESTONE_TYPE_BY_BLOCK_ID[blockId],
    );

const findExistingEmail = (
    existingBlock: CycleCommunicationBlock | undefined,
    audience: CommunicationEmailAudienceValue,
): CycleCommunicationEmail | undefined =>
    existingBlock?.emails.find(
        (email) => email.recipient === COMMUNICATION_RECIPIENT_BY_AUDIENCE[audience],
    );

export interface ResolvedCommunicationEmailContent {
    enabled: boolean;
    title: string;
    message: Descendant[];
}

/**
 * A stored empty string is a deliberate clearing and must survive a reload as blank, so the
 * presence of the key decides — never `??` or a truthiness test on the key itself.
 */
export const resolveCommunicationEmailContent = ({
    communications,
    blockId,
    audience,
    language,
    defaults,
}: {
    communications: readonly CycleCommunicationBlock[] | undefined;
    blockId: CommunicationsLayerBlockIdValue;
    audience: CommunicationEmailAudienceValue;
    language: string;
    defaults: EmailDefaults;
}): ResolvedCommunicationEmailContent => {
    const existingEmail = findExistingEmail(
        findExistingCommunicationBlock(communications, blockId),
        audience,
    );

    const storedTitle = existingEmail?.title[language];
    const storedBody = existingEmail?.body[language];

    // `typeof` and not `!== undefined`: nothing guarantees the server sent a string for this key.
    const title = typeof storedTitle === 'string' ? storedTitle : defaults.title;
    const message =
        storedBody !== undefined
            ? storedBody
                ? htmlToSlate(storedBody)
                : getEmptyEmailMessage()
            : defaults.body
              ? htmlToSlate(defaults.body)
              : getEmptyEmailMessage();

    return {
        enabled: existingEmail?.isEnabled ?? true,
        title,
        message,
    };
};

export const resolveCommunicationBlockEnabled = (
    communications: readonly CycleCommunicationBlock[] | undefined,
    blockId: CommunicationsLayerBlockIdValue,
): boolean => findExistingCommunicationBlock(communications, blockId)?.isEnabled ?? true;

export interface CommunicationEmailCardSnapshot {
    enabled: boolean;
    customizedLanguages: ReadonlySet<string>;
    seedLanguage: string;
    titleByLanguage: Record<string, string>;
    messageByLanguage: Record<string, Descendant[]>;
}

export const isEmptyDictionary = (dictionary: Record<string, unknown>): boolean =>
    Object.keys(dictionary).length === 0;

const buildEmailPayload = ({
    audience,
    card,
    existingBlock,
}: {
    audience: CommunicationEmailAudienceValue;
    card: CommunicationEmailCardSnapshot;
    existingBlock: CycleCommunicationBlock | undefined;
}) => {
    const existingEmail = findExistingEmail(existingBlock, audience);
    const title = { ...existingEmail?.title };
    const body = { ...existingEmail?.body };

    for (const language of Object.keys(card.titleByLanguage)) {
        if (card.titleByLanguage[language] !== undefined) {
            title[language] = card.titleByLanguage[language];
        }
    }

    for (const language of Object.keys(card.messageByLanguage)) {
        if (card.messageByLanguage[language] !== undefined) {
            body[language] = slateToHtml(card.messageByLanguage[language]);
        }
    }

    if (isEmptyDictionary(title) && card.titleByLanguage[card.seedLanguage] !== undefined) {
        title[card.seedLanguage] = card.titleByLanguage[card.seedLanguage];
    }

    if (isEmptyDictionary(body) && card.messageByLanguage[card.seedLanguage] !== undefined) {
        body[card.seedLanguage] = slateToHtml(card.messageByLanguage[card.seedLanguage]);
    }

    return {
        recipient: COMMUNICATION_RECIPIENT_BY_AUDIENCE[audience],
        isEnabled: card.enabled,
        title,
        body,
    };
};

/**
 * `communications` is a full-replacement contract — the server drops every block the payload does
 * not name — and a section hidden by an untick must keep the wording written into it.
 */
export const carryForwardUntouchedCommunicationBlocks = ({
    existingBlocks,
    writtenBlocks,
}: {
    existingBlocks: readonly CycleCommunicationBlock[] | undefined;
    writtenBlocks: readonly CycleCommunicationBlockPayload[];
}): CycleCommunicationBlockPayload[] => {
    const writtenMilestoneTypes = new Set(writtenBlocks.map((block) => block.milestoneType));

    return (existingBlocks ?? [])
        .filter((block) => !writtenMilestoneTypes.has(block.milestoneType))
        .map((block) => ({
            milestoneType: block.milestoneType,
            isEnabled: block.isEnabled,
            emails: block.emails.map((email) => ({
                recipient: email.recipient,
                title: email.title,
                body: email.body,
                isEnabled: email.isEnabled,
            })),
        }));
};

export interface BuildCommunicationBlockPayloadParams {
    blockId: CommunicationsLayerBlockIdValue;
    blockEnabled: boolean;
    managerCard: CommunicationEmailCardSnapshot;
    employeeCard?: CommunicationEmailCardSnapshot;
    communications: readonly CycleCommunicationBlock[] | undefined;
}

/**
 * The back-end validator refuses an employee email on the two manager-only blocks, so this site
 * must never manufacture one when `employeeCard` is absent.
 */
export const buildCommunicationBlockPayload = ({
    blockId,
    blockEnabled,
    managerCard,
    employeeCard,
    communications,
}: BuildCommunicationBlockPayloadParams): CycleCommunicationBlockPayload => {
    const existingBlock = findExistingCommunicationBlock(communications, blockId);

    const emails = [
        buildEmailPayload({
            audience: CommunicationEmailAudience.MANAGER,
            card: managerCard,
            existingBlock,
        }),
    ];

    if (employeeCard) {
        emails.push(
            buildEmailPayload({
                audience: CommunicationEmailAudience.EMPLOYEE,
                card: employeeCard,
                existingBlock,
            }),
        );
    }

    return {
        milestoneType: COMMUNICATION_MILESTONE_TYPE_BY_BLOCK_ID[blockId],
        isEnabled: blockEnabled,
        emails,
    };
};
